import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getMissingFirebaseConfig, isFirebaseConfigured } from "@/app/config/env";
import type { User } from "@/domain/user/user.types";
import { subscribeToAuthChanges } from "./authSubscription";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs to avoid re-creating callbacks
  const userRef = useRef(user);
  userRef.current = user;

  const loadUser = useCallback(async (firebaseUser: { uid: string } | null) => {
    if (!firebaseUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // Lazy import — only loads Firebase when needed
      const { FirestoreUserRepository } = await import("@/infrastructure/firestore/FirestoreUserRepository");
      const userRepo = new FirestoreUserRepository();
      const profile = await userRepo.getById(firebaseUser.uid);

      if (profile && profile.active) {
        setUser(profile);
        setError(null);
        // Link OneSignal user to Firebase UID (lazy)
        const { setOneSignalUser } = await import("@/infrastructure/onesignal/client");
        setOneSignalUser(firebaseUser.uid).catch(console.error);
      } else {
        setUser(null);
        setError("Account not found or deactivated.");
      }
    } catch (err) {
      setUser(null);
      setError("Failed to load user profile.");
      console.error("Auth error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured()) {
      // Firebase not configured — show error, require real credentials
      setError(`Firebase is not configured. Missing: ${getMissingFirebaseConfig().join(", ")}`);
      setLoading(false);
      return;
    }

    // Lazy-init OneSignal only when Firebase is configured
    import("@/infrastructure/onesignal/client").then(({ initOneSignal }) => {
      initOneSignal().catch(console.error);
    });

    // Lazy-init Firebase auth listener
    return subscribeToAuthChanges(
      () => import("@/infrastructure/firebase/auth"),
      (firebaseUser) => {
        loadUser(firebaseUser);
      },
    );
  }, [loadUser]);

  const refreshUser = useCallback(async () => {
    if (userRef.current) {
      const { FirestoreUserRepository } = await import("@/infrastructure/firestore/FirestoreUserRepository");
      const userRepo = new FirestoreUserRepository();
      const updated = await userRepo.getById(userRef.current.uid);
      if (updated) setUser(updated);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Lazy unlink OneSignal
      const { removeOneSignalUser } = await import("@/infrastructure/onesignal/client");
      await removeOneSignalUser();

      if (isFirebaseConfigured()) {
        const { signOut: fbSignOut } = await import("@/infrastructure/firebase/auth");
        await fbSignOut();
      }
      setUser(null);
    } catch (error) {
      // Re-throw the error so calling components can catch it
      throw error;
    }
  }, []);

  // Memoize context value — only recreate when user/loading/error changes
  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, refreshUser, signOut }),
    [user, loading, error, refreshUser, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
